import i18n from '@lib/i18n';
import { uniq, uniqBy } from 'lodash';
import { LocaleField } from './dataTypes';
type LabelType = 'local' | 'native';

type LanguageOptionStorage = Record<string, { locale_name: string; native_name: string }>;

export interface LanguageOption {
  label: string;
  value: string;
}

export const SUPPORTED_LOCALES: LocaleField[] = ['en-US', 'es-US', 'en-CA', 'fr-CA', 'en-GB'];

// Catalog/details use local labels, while the header language picker uses native labels.
const languageLabels: Record<LabelType, Record<string, string>> = {
  local: {
    en: i18n.t('English'),
    es: i18n.t('Spanish'),
    'fr-CA': i18n.t('French (Canada)'),
  },
  native: {
    en: 'English',
    es: 'Español',
    'fr-CA': 'Français (Canada)',
  },
};

function normalizeLocale(locale?: string): string {
  return (locale || '').replace('_', '-');
}

function getLanguageLabel(locale: string, labelType: LabelType): string {
  const normalizedLocale = normalizeLocale(locale);
  const languageCode = normalizedLocale.split('-')[0]?.toLowerCase();

  // English and Spanish locales collapse to one visible label; fr-CA keeps its distinct Canada label.
  return languageLabels[labelType][normalizedLocale] || languageLabels[labelType][languageCode] || normalizedLocale;
}

function getLanguageLabelList(locales: string[], labelType: LabelType): string[] {
  return uniq(locales.map(locale => getLanguageLabel(locale, labelType)).filter(Boolean));
}

function getUniqueLanguageOptions(languageOptions: LanguageOption[]): LanguageOption[] {
  return uniqBy(languageOptions, 'label');
}

export function getNativeLanguageLabels(langArr: string[] = []): string[] {
  return getLanguageLabelList(langArr, 'native');
}

export function getLocalLanguageOptions(languages: LanguageOptionStorage): LanguageOption[] {
  const languageOptions = Object.values(languages).map(lang => {
    return {
      label: getLanguageLabel(lang.locale_name, 'local'),
      value: lang.locale_name,
    };
  });

  // Filters should only show one visible option per language label, even if multiple locales map to it.
  return getUniqueLanguageOptions(languageOptions);
}

export function getLocalLanguageOptionsFromStorage(): LanguageOption[] {
  const languageOptions = JSON.parse(localStorage.getItem('languages') || '{}');

  return getLocalLanguageOptions(languageOptions);
}

export function getLanguageLabels(langArr: string[] = []): string[] {
  return getLanguageLabelList(langArr, 'local');
}
